// imports
import { Address } from 'abitype';
import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '../..';
import { logAnalytics } from '../../../../services/firebase';
import { HeroNFT, IQuestStartResponse } from '../../../web3/types';
import { IUserPreferences, IUserPreferencesBody } from '../../../api';
import {
  Quest, QuestCost,
  IWallet, QuestStatus, QuestType
} from '../../../../common/types';
import {
  fetchHeroes as fetchHeroesThunk,
  updateHeroesChain as updateHeroesChainThunk,
  fetchBackpackItems as fetchBackpackItemsThunk,
  startQuest as startQuestThunk,
  claimRewards as claimRewardsThunk,
  completeQuest as completeQuestThunk,
  fetchQuestsProgress as fetchQuestsProgressThunk,
  fetchPreferences as fetchPreferencesThunk,
  updatePreferences as updatePreferencesThunk,
  resetPreferencesUpdateStatus as resetPreferencesUpdateStatusThunk,
  fetchDelegateWallet as fetchDelegateWalletThunk,
  resetDelegateWallet as resetDelegateWalletThunk,
  updateDelegateWallet as updateDelegateWalletThunk
} from '../../thunks';

// interfaces / types / consts
const DEFAULT_STATE = {
  id: '',
  backpack: {
    fetchError: '',
    fetchStatus: 'idle',
    items: {} as { [key: string]: number }
  },
  quests: {
    fetchError: '',
    fetchStatus: 'idle',
    startId: '',
    startError: '',
    claimId: '',
    claimError: '',
    campaignProgression: 0,
    unavailableIds: [] as string[],
    data: {} as { [key: string]: Quest }
  },
  heroes: {
    fetchError: '',
    fetchStatus: 'idle',
    nfts: [] as HeroNFT[]
  },
  preferences: {
    fetchError: '',
    updateError: '',
    fetchStatus: 'idle',
    updateStatus: 'idle',
    showCaptcha: false,
    bio: {
      email: '',
      username: '',
      fullname: ''
    },
    prompts: {
      avoidBio: false,
      avoidNotifications: false
    },
    notifications: {
      email: false,
      fcmToken: '',
      newQuests: false,
      questCompleted: false
    }
  },
  delegateWallet: {
    fetchError: '',
    updateError: '',
    fetchStatus: 'idle',
    updateStatus: 'idle',
    wallet: null as IWallet | null
  }
};

// redux slice
const userSlice = createSlice({
  name: 'user',
  initialState: { ...DEFAULT_STATE },
  reducers: {
    clearState: (state) => {
      state.id = '';
      state.quests = { ...DEFAULT_STATE.quests };
      state.heroes = { ...DEFAULT_STATE.heroes };
      state.backpack = { ...DEFAULT_STATE.backpack };
      state.preferences = { ...DEFAULT_STATE.preferences };
      state.delegateWallet = { ...DEFAULT_STATE.delegateWallet };
    },
    setCrewTraitMatching: (state, action) => {
      const { questId, crewTraitMatching } = action.payload;
      state.quests.data[questId] = { ...state.quests.data[questId], crewTraitMatching };
    }
  },
  extraReducers: (builder) => {
    // preferences - fetch
    builder.addCase(fetchPreferencesThunk.pending, (state, action) => {
      const wallet = action.meta.arg as Address;
      state.id = wallet;
      state.quests = { ...DEFAULT_STATE.quests };
      state.heroes = { ...DEFAULT_STATE.heroes };
      state.backpack = { ...DEFAULT_STATE.backpack };
      state.delegateWallet = { ...DEFAULT_STATE.delegateWallet };
      state.preferences = {
        ...DEFAULT_STATE.preferences,
        fetchStatus: 'loading'
      };
    });
    builder.addCase(fetchPreferencesThunk.fulfilled, (state, action) => {
      const preferences = action.payload as IUserPreferences;
      state.preferences = {
        ...DEFAULT_STATE.preferences,
        fetchError: '',
        fetchStatus: 'success',
        showCaptcha: preferences.showCaptcha,
        bio: { ...DEFAULT_STATE.preferences.bio, ...preferences.bio },
        prompts: { ...DEFAULT_STATE.preferences.prompts, ...preferences.prompts },
        notifications: { ...DEFAULT_STATE.preferences.notifications, ...preferences.notifications }
      };
    });
    builder.addCase(fetchPreferencesThunk.rejected, (state, action) => {
      state.preferences = { ...state.preferences, fetchStatus: 'error', fetchError: action.payload as string };
    });
    // preferences - update
    builder.addCase(updatePreferencesThunk.pending, (state) => {
      state.preferences.updateError = '';
      state.preferences.updateStatus = 'loading';
    });
    builder.addCase(updatePreferencesThunk.fulfilled, (state, action) => {
      const body = action.meta.arg as IUserPreferencesBody;
      state.preferences = {
        ...state.preferences,
        updateError: '',
        updateStatus: 'success',
        bio: { ...state.preferences.bio, ...body.bio },
        prompts: { ...state.preferences.prompts, ...body.prompts },
        notifications: { ...state.preferences.notifications, ...body.notifications }
      };
    });
    builder.addCase(updatePreferencesThunk.rejected, (state, action) => {
      state.preferences = { ...state.preferences, updateStatus: 'error', updateError: action.payload as string };
    });
    // preferences - reset
    builder.addCase(resetPreferencesUpdateStatusThunk.fulfilled, (state) => {
      state.preferences.updateStatus = 'idle';
    });
    // delegateWallet - fetch
    builder.addCase(fetchDelegateWalletThunk.pending, (state) => {
      state.delegateWallet = {
        ...DEFAULT_STATE.delegateWallet,
        fetchStatus: 'loading'
      };
    });
    builder.addCase(fetchDelegateWalletThunk.fulfilled, (state, action) => {
      const wallet = action.payload as IWallet | null;
      state.delegateWallet = {
        ...state.delegateWallet,
        wallet,
        fetchError: '',
        fetchStatus: 'success'
      };
    });
    builder.addCase(fetchDelegateWalletThunk.rejected, (state, action) => {
      state.delegateWallet = { ...state.delegateWallet, fetchStatus: 'error', fetchError: action.payload as string };
    });
    // delegateWallet - update
    builder.addCase(updateDelegateWalletThunk.pending, (state) => {
      state.delegateWallet = {
        ...state.delegateWallet,
        updateError: '',
        updateStatus: 'loading'
      };
      // Analytics logging
      logAnalytics({ action: 'CLICK', name: 'DELEGATE_WALLET' });
    });
    builder.addCase(updateDelegateWalletThunk.fulfilled, (state, action) => {
      const wallet = action.payload as IWallet;
      state.delegateWallet = {
        ...state.delegateWallet,
        wallet,
        updateError: '',
        updateStatus: 'success'
      };
      // Analytics logging
      logAnalytics({ action: 'SUCCESS', name: 'DELEGATE_WALLET', eventParams: { walletId: wallet.address } });
    });
    builder.addCase(updateDelegateWalletThunk.rejected, (state, action) => {
      state.delegateWallet = { ...state.delegateWallet, updateStatus: 'error', updateError: action.payload as string };
    });
    // delegateWallet - reset
    builder.addCase(resetDelegateWalletThunk.pending, (state) => {
      state.delegateWallet = {
        ...state.delegateWallet,
        updateError: '',
        updateStatus: 'loading'
      };
    });
    builder.addCase(resetDelegateWalletThunk.fulfilled, (state, action) => {
      const wallet = action.payload as IWallet;
      state.delegateWallet = {
        ...state.delegateWallet,
        wallet,
        fetchError: '',
        updateError: '',
        fetchStatus: 'idle',
        updateStatus: 'idle'
      };
    });
    builder.addCase(resetDelegateWalletThunk.rejected, (state, action) => {
      state.delegateWallet = { ...state.delegateWallet, updateStatus: 'error', updateError: action.payload as string };
    });
    // quests - fetch
    builder.addCase(fetchQuestsProgressThunk.pending, (state) => {
      state.quests.fetchError = '';
      state.quests.fetchStatus = 'loading';
    });
    builder.addCase(fetchQuestsProgressThunk.fulfilled, (state, action) => {
      state.quests.fetchError = '';
      state.quests.fetchStatus = 'success';
      const { data, unavailableIds, campaignProgression } = action.payload;
      state.quests.data = data;
      state.quests.unavailableIds = unavailableIds;
      state.quests.campaignProgression = campaignProgression;
    });
    builder.addCase(fetchQuestsProgressThunk.rejected, (state, action) => {
      state.quests.fetchStatus = 'error';
      state.quests.fetchError = action.payload as string;
    });
    // quests - start
    builder.addCase(startQuestThunk.pending, (state, action) => {
      const {
        questId: id, heroTokens: crew, rewardChances, isCampaign
      } = action.meta.arg;
      state.quests.startId = id;
      state.quests.startError = '';
      // Analytics logging
      logAnalytics({
        action: 'CLICK',
        name: 'QUEST_START',
        eventParams: {
          questId: id,
          party_cap: 3,
          selected_heroes: crew,
          party_size: crew.filter((heroId) => heroId !== 0).length,
          quest_type: isCampaign ? QuestType.Campaign : QuestType.Side,
          reward_chances: rewardChances
        }
      });
    });
    builder.addCase(startQuestThunk.fulfilled, (state, action) => {
      const {
        questId: id, heroTokens: crew, crewTraitMatching, isCampaign
      } = action.meta.arg;
      const { endTime, startTime, rewardChances } = action.payload as IQuestStartResponse;
      const quest: Quest = state.quests.data[id];
      state.quests.startId = '';
      state.quests.startError = '';
      state.quests.data[id] = {
        ...quest,
        crew,
        endTime,
        startTime,
        rewardChances,
        crewTraitMatching,
        status: QuestStatus.inProgress
      };
      state.heroes.nfts = state.heroes.nfts.map((hero) => ({
        ...hero,
        locked: crew.includes(hero.tokenId) || hero.locked,
        lockedUntil: crew.includes(hero.tokenId) ? endTime : hero.lockedUntil
      }));
      quest.cost.forEach((cost) => {
        const { itemId, amount } = cost;
        state.backpack.items[itemId] -= amount;
      });
      // Analytics logging
      logAnalytics({
        action: 'SUCCESS',
        name: 'QUEST_START',
        eventParams: {
          questId: id,
          party_cap: 3,
          selected_heroes: crew,
          party_size: crew.filter((heroId) => heroId !== 0).length,
          quest_type: isCampaign ? QuestType.Campaign : QuestType.Side,
          reward_chances: rewardChances
        }
      });
      quest.cost.forEach((cost) => {
        const { itemId, amount } = cost;
        logAnalytics({ action: 'SUCCESS', name: 'COST_SPENT', eventParams: { questId: id, itemId, amount } });
      });
      crew.forEach((heroId) => {
        if (heroId !== 0) {
          logAnalytics({ action: 'SUCCESS', name: 'HERO_SENT', eventParams: { questId: id, heroId } });
        }
      });
    });
    builder.addCase(startQuestThunk.rejected, (state, action) => {
      state.quests.startId = '';
      state.quests.startError = action.payload as string;
    });
    // quests - complete
    builder.addCase(completeQuestThunk.fulfilled, (state, action) => {
      const questId = action.payload;
      state.quests.data[questId].status = QuestStatus.completed;
    });
    // quests - claim
    builder.addCase(claimRewardsThunk.pending, (state, action) => {
      const { questId: id } = action.meta.arg;
      state.quests.claimId = id;
      state.quests.claimError = '';
      // Analytics logging
      logAnalytics({ action: 'CLICK', name: 'REWARD_CLAIM', eventParams: { questId: id } });
    });
    builder.addCase(claimRewardsThunk.fulfilled, (state, action) => {
      const { questId: id } = action.meta.arg;
      const rewards = action.payload as QuestCost[];
      const quest: Quest = { ...state.quests.data[id] };
      if (quest.type === QuestType.Campaign) {
        delete state.quests.data[id];
        const campaignProgression = state.quests.campaignProgression + 1;
        state.quests.campaignProgression = campaignProgression;
        const nextPlayableCampaignId = `camp_${`${campaignProgression + 1}`.padStart(3, '0')}`;
        if (state.quests.data[nextPlayableCampaignId]) { state.quests.data[nextPlayableCampaignId].status = QuestStatus.available; }
      } else if (state.quests.unavailableIds.includes(id)) {
        delete state.quests.data[id];
      } else {
        delete state.quests.data[id].endTime;
        delete state.quests.data[id].startTime;
        delete state.quests.data[id].rewardChances;
        state.quests.data[id].crew = [0, 0, 0];
        state.quests.data[id].crewTraitMatching = [];
        state.quests.data[id].status = QuestStatus.available;
      }
      const crew = quest.crew!;
      state.quests.claimId = '';
      state.quests.claimError = '';
      state.heroes.nfts = state.heroes.nfts.map((hero) => ({
        ...hero,
        locked: crew.includes(hero.tokenId) ? false : hero.locked,
        lockedUntil: crew.includes(hero.tokenId) ? 0 : hero.lockedUntil
      }));
      rewards.forEach((reward) => {
        const count = (state.backpack.items[reward.itemId] || 0) + reward.amount;
        state.backpack.items[reward.itemId] = count;
      });
      // Analytics logging
      logAnalytics({
        action: 'SUCCESS',
        name: 'REWARD_CLAIM',
        eventParams: {
          questId: id
        }
      });
      crew.forEach((heroId) => {
        if (heroId !== 0) {
          logAnalytics({ action: 'SUCCESS', name: 'HERO_RETURNED', eventParams: { questId: id, heroId } });
        }
      });
      rewards.forEach((reward) => {
        const { itemId, amount } = reward;
        if (amount !== 0) {
          logAnalytics({ action: 'SUCCESS', name: 'REWARD_ITEM_ADD', eventParams: { questId: id, itemId, amount } });
        }
      });
    });
    builder.addCase(claimRewardsThunk.rejected, (state, action) => {
      state.quests.claimId = '';
      state.quests.claimError = action.payload as string;
    });
    // inventory - heroes - fetch
    builder.addCase(fetchHeroesThunk.pending, (state) => {
      state.heroes.nfts = [];
      state.heroes.fetchError = '';
      state.heroes.fetchStatus = 'loading';
    });
    builder.addCase(fetchHeroesThunk.fulfilled, (state, action) => {
      const heroes = action.payload as HeroNFT[];
      state.heroes.nfts = heroes;
      state.heroes.fetchError = '';
      state.heroes.fetchStatus = 'success';
    });
    builder.addCase(fetchHeroesThunk.rejected, (state, action) => {
      state.heroes = { ...state.heroes, fetchStatus: 'error', fetchError: action.payload as string };
    });
    // inventory - heroes - updateChain
    builder.addCase(updateHeroesChainThunk.pending, (state, action) => {
      const { tokenIds, targetChain } = action.meta.arg;
      state.heroes.nfts = state.heroes.nfts.map((nft) => ({ ...nft, chain: tokenIds.includes(nft.tokenId) ? 'Transit' : nft.chain }));
      state.heroes.fetchError = '';
      state.heroes.fetchStatus = 'loading';
      // Analytics logging
      const eventName = targetChain === 'L2' ? 'ACTIVATE_HEROES' : 'DEACTIVATE_HEROES';
      logAnalytics({ action: 'CLICK', name: eventName, eventParams: { heroIds: tokenIds } });
    });
    builder.addCase(updateHeroesChainThunk.fulfilled, (state, action) => {
      const { tokenIds, targetChain } = action.meta.arg;
      state.heroes.fetchError = '';
      state.heroes.fetchStatus = 'success';
      // Analytics logging
      const eventName = targetChain === 'L2' ? 'ACTIVATE_HEROES' : 'DEACTIVATE_HEROES';
      logAnalytics({ action: 'SUCCESS', name: eventName, eventParams: { heroIds: tokenIds } });
    });
    builder.addCase(updateHeroesChainThunk.rejected, (state, action) => {
      const { targetChain, tokenIds } = action.meta.arg;
      const originalChain = targetChain === 'L1' ? 'L2' : 'L1';
      state.heroes.nfts = state.heroes.nfts.map((nft) => ({ ...nft, chain: tokenIds.includes(nft.tokenId) ? originalChain : nft.chain } as HeroNFT));
      state.heroes.fetchStatus = 'error';
      state.heroes.fetchError = action.payload as string;
    });
    // inventory - backpack - fetch
    builder.addCase(fetchBackpackItemsThunk.pending, (state) => {
      state.backpack.fetchError = '';
      state.backpack.fetchStatus = 'loading';
    });
    builder.addCase(fetchBackpackItemsThunk.fulfilled, (state, action) => {
      const backpackItems = action.payload as { [key: string]: number };
      state.backpack = {
        fetchError: '',
        items: backpackItems,
        fetchStatus: 'success'
      };
    });
    builder.addCase(fetchBackpackItemsThunk.rejected, (state, action) => {
      state.backpack = { ...state.backpack, fetchStatus: 'error', fetchError: action.payload as string };
    });
  }
});

// exports
export const { clearState, setCrewTraitMatching } = userSlice.actions;
export const selectUserId = (state: RootState): typeof state.user.id => state.user.id;
export const selectUserHeroes = (state: RootState): typeof state.user.heroes => state.user.heroes;
export const selectUserQuests = (state: RootState): typeof state.user.quests => state.user.quests;
export const selectUserBackpack = (state: RootState): typeof state.user.backpack => state.user.backpack;
export const selectUserPreferences = (state: RootState): typeof state.user.preferences => state.user.preferences;
export const selectUserDelegateWallet = (state: RootState): typeof state.user.delegateWallet => state.user.delegateWallet;
export default userSlice.reducer;
